import { api } from './api';

export interface DashboardMetrics {
  // YYYY-MM-DD, both ends included; null when the period is open on that side
  period: { from: string | null; to: string | null };
  totalSales: number;
  salesOrders: number;
  // Change against the period of the same length just before; null when there is nothing to compare with
  salesGrowth: number | null;
  totalPurchases: number;
  purchaseOrders: number;
  purchaseGrowth: number | null;
  averageOrderValue: number;
  // Sales minus purchases, and that as a share of sales
  netPosition: number;
  profitMargin: number;
  // Invoices issued in the period (paid ones included)
  totalInvoices: number;
  invoicedValue: number;
  // Quotes raised in the period that are still quotes
  openQuotes: number;
  openQuoteValue: number;
  totalProducts: number;
  inventoryValue: number;
}

export type TrendBucket = 'day' | 'week' | 'month';

export interface SalesAnalytics {
  bucket: TrendBucket;
  // Sales per day, week or month, depending on the length of the period
  trend: { period: string; amount: number; orders: number }[];
  monthlySales: { month: string; amount: number; orders: number }[];
  topProducts: { productId: string; productName: string; sku: string; quantity: number; revenue: number }[];
  salesByStatus: { status: string; count: number; value: number }[];
  customerAnalytics: { customerId: string; customerName: string; totalOrders: number; totalValue: number }[];
}

export interface InventoryAnalytics {
  totals: { totalProducts: number; stockValue: number; lowStock: number; outOfStock: number };
  lowStockItems: {
    productId: string;
    productName: string;
    sku: string;
    unit: string;
    currentStock: number;
    minimumStock: number;
    reorderLevel: number;
  }[];
  fastMovingProducts: { productId: string; productName: string; totalSold: number; revenue: number }[];
  inventoryValue: { category: string; value: number; items: number }[];
  // Units received and issued per month, from the stock ledger
  stockMovement: { month: string; stockIn: number; stockOut: number; value: number }[];
}

export interface FinancialAnalytics {
  monthlyRevenue: { month: string; revenue: number; profit: number; expenses: number }[];
  profitLoss: { category: string; amount: number; percentage: number }[];
  cashFlow: { month: string; cashIn: number; cashOut: number; netFlow: number }[];
  taxSummary: { taxType: string; amount: number; percentage: number }[];
  invoices: { issued: number; issuedValue: number; paidValue: number; outstandingValue: number };
}

export type OverviewRange = '30d' | '90d' | '12m' | 'all';

export interface PeriodFigure {
  value: number;
  orders: number;
  previousValue: number | null;
  changePercent: number | null;
}

export interface StatusCount {
  status: string;
  count: number;
  value?: number;
}

export interface DashboardOverview {
  range: OverviewRange;
  periodStart: string | null;
  periodEnd: string;
  generatedAt: string;
  sales: PeriodFigure;
  purchases: PeriodFigure;
  netPosition: number;
  averageSalesOrderValue: number;
  inventory: {
    totalProducts: number;
    stockValue: number;
    lowStock: number;
    outOfStock: number;
    overstocked: number;
    healthy: number;
  };
  parties: { activeCustomers: number; activeVendors: number };
  invoices: { unpaidCount: number; unpaidValue: number; overdueCount: number };
  topProducts: { productId: string; name: string; quantity: number; revenue: number }[];
  topCustomers: { id: string; name: string; orders: number; value: number }[];
  topVendors: { id: string; name: string; orders: number; value: number }[];
  recentStockMovements: {
    id: string;
    productName: string;
    sku: string;
    type: string;
    reason: string;
    quantity: number;
    newStock: number;
    createdAt: string;
  }[];
  openOrders: { sales: number; purchases: number; workOrders: number };
  attention: {
    lowStock: number;
    purchasePendingApproval: number;
    purchaseAwaitingReceipt: number;
    purchaseOverdue: number;
    salesOverdue: number;
    workOrdersOverdue: number;
  };
  monthlyTrend: { month: string; sales: number; purchases: number }[];
  salesByStatus: StatusCount[];
  purchasesByStatus: StatusCount[];
  workOrdersByStatus: StatusCount[];
  inventoryByCategory: { category: string; items: number; value: number }[];
  lowStockItems: {
    id: string;
    name: string;
    sku: string;
    unit: string;
    currentStock: number;
    minimumStock: number;
  }[];
  recentOrders: {
    type: 'sales' | 'purchase';
    id: string;
    orderNumber: string;
    party: string;
    orderDate: string;
    amount: number;
    status: string;
  }[];
}

export class AnalyticsService {
  static async getOverview(range: OverviewRange): Promise<DashboardOverview> {
    const response = await api.get<DashboardOverview>('/analytics/overview', { params: { range } });
    return response.data;
  }

  static async getDashboardMetrics(fromDate?: string, toDate?: string): Promise<DashboardMetrics> {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const response = await api.get(`/analytics/dashboard?${params.toString()}`);
    return response.data;
  }

  static async getSalesAnalytics(fromDate?: string, toDate?: string): Promise<SalesAnalytics> {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const response = await api.get(`/analytics/sales?${params.toString()}`);
    return response.data;
  }

  // The period only applies to the fast-moving products; stock figures are as of now
  static async getInventoryAnalytics(fromDate?: string, toDate?: string): Promise<InventoryAnalytics> {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);

    const response = await api.get(`/analytics/inventory?${params.toString()}`);
    return response.data;
  }

  static async getFinancialAnalytics(fromDate?: string, toDate?: string): Promise<FinancialAnalytics> {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const response = await api.get(`/analytics/financial?${params.toString()}`);
    return response.data;
  }

  static async exportSalesReport(fromDate?: string, toDate?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const response = await api.get(`/analytics/reports/sales/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  static async exportInventoryReport(): Promise<Blob> {
    const response = await api.get('/analytics/reports/inventory/export', {
      responseType: 'blob',
    });
    return response.data;
  }

  static async exportFinancialReport(fromDate?: string, toDate?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const response = await api.get(`/analytics/reports/financial/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  }
}